import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  FilterBar,
  ConfirmDeleteModal,
  SearchInput,
  FilterSelect,
  ResultSummary,
  useListControls,
} from '../components/crud';

const PAGE_SIZE = 25;

const PermissionComponent = () => {
  const [permissions, setPermissions] = useState([]);
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  const [userTypes, setUserTypes] = useState([]);

  const [pendingRows, setPendingRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const { authState } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authState) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [authState, navigate]);

  const fetchData = async () => {
    const fetchEndpoints = ['/api/resources', '/api/actions', '/api/usertypes', '/api/permissions'];
    const results = await Promise.all(
      fetchEndpoints.map((endpoint) =>
        fetch(`${backendURL}${endpoint}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }).then((response) => response.json())
      )
    );

    setResources(Array.isArray(results[0]) ? results[0] : []);
    setActions(Array.isArray(results[1]) ? results[1] : []);
    setUserTypes(Array.isArray(results[2]) ? results[2] : []);
    setPermissions(Array.isArray(results[3]) ? results[3] : []);
  };

  const sortedActions = useMemo(
    () => [...actions].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [actions]
  );

  const userTypeMap = useMemo(() => {
    const map = new Map();
    userTypes.forEach((u) => map.set(u.type_id, u.type));
    return map;
  }, [userTypes]);

  const resourceMap = useMemo(() => {
    const map = new Map();
    resources.forEach((r) => map.set(r.resource_id, r.name));
    return map;
  }, [resources]);

  const existingRows = useMemo(() => {
    const grouped = new Map();

    permissions.forEach((permission) => {
      const key = `${permission.type_id}-${permission.resource_id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          rowType: 'existing',
          key,
          type_id: permission.type_id,
          resource_id: permission.resource_id,
          actionsById: new Map(),
        });
      }

      grouped.get(key).actionsById.set(permission.action_id, permission);
    });

    return Array.from(grouped.values());
  }, [permissions]);

  const pendingDisplayRows = useMemo(
    () =>
      pendingRows.map((row) => ({
        ...row,
        rowType: 'pending',
        key: `pending-${row.temp_id}`,
      })),
    [pendingRows]
  );

  const allRows = useMemo(() => [...pendingDisplayRows, ...existingRows], [pendingDisplayRows, existingRows]);

  // Compound sort (user type, then resource) stays outside the hook, so the hook
  // runs search + filters only (sortable: false) and preserves this ordering.
  const sortedRows = useMemo(
    () =>
      [...allRows].sort((a, b) => {
        const aType = userTypeMap.get(parseInt(a.type_id, 10)) || '';
        const bType = userTypeMap.get(parseInt(b.type_id, 10)) || '';
        if (aType !== bType) return aType.localeCompare(bType);

        const aResource = resourceMap.get(parseInt(a.resource_id, 10)) || '';
        const bResource = resourceMap.get(parseInt(b.resource_id, 10)) || '';
        return aResource.localeCompare(bResource);
      }),
    [allRows, userTypeMap, resourceMap]
  );

  const listContext = useMemo(
    () => ({ userTypeMap, resourceMap, sortedActions }),
    [userTypeMap, resourceMap, sortedActions]
  );

  // Search matches user type, resource, each action name, and its yes/no/n-a label.
  const permissionSearchAccessor = useMemo(
    () => (row, ctx) => {
      const rowTypeId = row.type_id ? parseInt(row.type_id, 10) : null;
      const rowResourceId = row.resource_id ? parseInt(row.resource_id, 10) : null;
      const parts = [
        ctx.userTypeMap.get(rowTypeId) || '',
        ctx.resourceMap.get(rowResourceId) || '',
      ];
      ctx.sortedActions.forEach((action) => {
        let accessLabel = 'n/a';
        if (row.rowType === 'pending') {
          accessLabel = row.actionChecks?.[action.action_id] ? 'yes' : 'n/a';
        } else {
          const permission = row.actionsById.get(action.action_id);
          if (permission) accessLabel = permission.can_access ? 'yes' : 'no';
        }
        parts.push(action.name || '', accessLabel);
      });
      return parts.join(' ');
    },
    []
  );

  const permissionFilters = useMemo(
    () => [
      {
        key: 'userType',
        label: 'User Type',
        allOption: { value: '', label: 'All User Types' },
        options: userTypes.map((type) => ({
          value: String(type.type_id),
          label: type.type,
        })),
        predicate: (row, value) => parseInt(row.type_id, 10) === parseInt(value, 10),
      },
      {
        key: 'resource',
        label: 'Resource',
        allOption: { value: '', label: 'All Resources' },
        options: resources.map((resource) => ({
          value: String(resource.resource_id),
          label: resource.name,
        })),
        predicate: (row, value) =>
          parseInt(row.resource_id, 10) === parseInt(value, 10),
      },
      {
        key: 'action',
        label: 'Action Filter',
        allOption: { value: '', label: 'All Actions' },
        options: sortedActions.map((action) => ({
          value: String(action.action_id),
          label: action.name,
        })),
        predicate: (row, value) => {
          const actionId = parseInt(value, 10);
          return row.rowType === 'pending'
            ? Boolean(row.actionChecks?.[actionId])
            : row.actionsById.has(actionId);
        },
      },
    ],
    [userTypes, resources, sortedActions]
  );

  const {
    items: filteredRows,
    resultCount,
    totalCount,
    isFiltered,
    filterSignature,
    search,
    filterList,
    filters: filterControls,
    clear,
  } = useListControls(sortedRows, {
    searchAccessor: permissionSearchAccessor,
    filters: permissionFilters,
    sortable: false,
    context: listContext,
  });

  // Keyed on the actual search/filter state, not resultCount — two filters can
  // yield equal counts and the page must still reset.
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const paginatedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const addPendingRow = () => {
    const tempId = Date.now() + Math.floor(Math.random() * 1000);

    setPendingRows((prev) => [
      {
        temp_id: tempId,
        type_id: filterControls.userType?.value || '',
        resource_id: filterControls.resource?.value || '',
        actionChecks: {},
      },
      ...prev,
    ]);
  };

  const updatePendingRowField = (tempId, field, value) => {
    setPendingRows((prev) =>
      prev.map((row) => (row.temp_id === tempId ? { ...row, [field]: value } : row))
    );
  };

  const updatePendingCheckbox = (tempId, actionId, checked) => {
    setPendingRows((prev) =>
      prev.map((row) => {
        if (row.temp_id !== tempId) return row;

        return {
          ...row,
          actionChecks: {
            ...(row.actionChecks || {}),
            [actionId]: checked,
          },
        };
      })
    );
  };

  const savePendingRow = async (row) => {
    const typeId = parseInt(row.type_id, 10);
    const resourceId = parseInt(row.resource_id, 10);

    if (!typeId || !resourceId) {
      alert('Please select both User Type and Resource before saving.');
      return;
    }

    const checkedActions = sortedActions.filter((action) => row.actionChecks?.[action.action_id]);

    if (checkedActions.length === 0) {
      alert('Check at least one permission box to save this new row.');
      return;
    }

    await Promise.all(
      checkedActions.map((action) =>
        fetch(`${backendURL}/api/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type_id: typeId,
            resource_id: resourceId,
            action_id: action.action_id,
            can_access: true,
          }),
        })
      )
    );

    setPendingRows((prev) => prev.filter((pending) => pending.temp_id !== row.temp_id));
    fetchData();
  };

  const toggleExistingPermission = async (row, actionId, checked) => {
    const permission = row.actionsById.get(actionId);

    if (permission) {
      await fetch(`${backendURL}/api/permissions/${permission.permission_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...permission,
          can_access: checked,
        }),
      });

      fetchData();
      return;
    }

    if (checked) {
      await fetch(`${backendURL}/api/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type_id: parseInt(row.type_id, 10),
          resource_id: parseInt(row.resource_id, 10),
          action_id: actionId,
          can_access: true,
        }),
      });

      fetchData();
    }
  };

  const promptRemoveRow = (row) => {
    if (row.rowType === 'pending') {
      setPendingRows((prev) => prev.filter((pending) => pending.temp_id !== row.temp_id));
      return;
    }

    const permissionIds = Array.from(row.actionsById.values()).map((permission) => permission.permission_id);
    setRowToDelete({ ...row, permissionIds });
    setShowModal(true);
  };

  const deleteRow = async () => {
    if (!rowToDelete || !Array.isArray(rowToDelete.permissionIds)) {
      setShowModal(false);
      return;
    }

    await Promise.all(
      rowToDelete.permissionIds.map((permissionId) =>
        fetch(`${backendURL}/api/permissions/${permissionId}`, {
          method: 'DELETE',
          credentials: 'include',
        })
      )
    );

    setShowModal(false);
    setRowToDelete(null);
    fetchData();
  };

  return (
    <div>
      <Button onClick={addPendingRow} className="m-1">Add New Permission</Button>

      <FilterBar
        summary={(
          <ResultSummary
            count={resultCount}
            total={totalCount}
            noun="permission row"
            nounPlural="permission rows"
            onClear={clear}
            isFiltered={isFiltered}
          />
        )}
      >
        <SearchInput
          label="Search"
          placeholder="User type, resource, action, yes/no"
          {...search}
        />
        {filterList.map((f) => (
          <FilterSelect key={f.filterKey} {...f} />
        ))}
      </FilterBar>

      <div className="d-flex justify-content-end align-items-center mb-2">
        <div>
          Page {page} of {totalPages}
        </div>
      </div>

      <div className="scrollable-table">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>User Type</th>
              <th>Resource</th>
              {sortedActions.map((action) => (
                <th key={action.action_id}>{action.name}</th>
              ))}
              <th>Row Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row) => (
                <tr key={row.key}>
                  <td>
                    {row.rowType === 'pending' ? (
                      <Form.Select
                        value={row.type_id}
                        onChange={(e) => updatePendingRowField(row.temp_id, 'type_id', e.target.value)}
                      >
                        <option value="">Select User Type</option>
                        {userTypes.map((type) => (
                          <option key={type.type_id} value={type.type_id}>
                            {type.type}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      userTypeMap.get(parseInt(row.type_id, 10)) || 'N/A'
                    )}
                  </td>
                  <td>
                    {row.rowType === 'pending' ? (
                      <Form.Select
                        value={row.resource_id}
                        onChange={(e) => updatePendingRowField(row.temp_id, 'resource_id', e.target.value)}
                      >
                        <option value="">Select Resource</option>
                        {resources.map((resource) => (
                          <option key={resource.resource_id} value={resource.resource_id}>
                            {resource.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      resourceMap.get(parseInt(row.resource_id, 10)) || 'N/A'
                    )}
                  </td>

                  {sortedActions.map((action) => {
                    const actionId = action.action_id;
                    let isChecked = false;

                    if (row.rowType === 'pending') {
                      isChecked = Boolean(row.actionChecks?.[actionId]);
                    } else {
                      const permission = row.actionsById.get(actionId);
                      isChecked = Boolean(permission?.can_access);
                    }

                    return (
                      <td key={`${row.key}-${actionId}`} className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (row.rowType === 'pending') {
                              updatePendingCheckbox(row.temp_id, actionId, e.target.checked);
                            } else {
                              toggleExistingPermission(row, actionId, e.target.checked);
                            }
                          }}
                        />
                      </td>
                    );
                  })}

                  <td>
                    {row.rowType === 'pending' ? (
                      <>
                        <Button size="sm" className="m-1" onClick={() => savePendingRow(row)}>
                          Save
                        </Button>
                        <Button size="sm" variant="secondary" className="m-1" onClick={() => promptRemoveRow(row)}>
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="danger" className="m-1" onClick={() => promptRemoveRow(row)}>
                        Remove Resource
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3 + sortedActions.length} className="text-center">
                  No permissions found for current filters.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-2">
        <Button variant="secondary" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={page === totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>

      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteRow}
        title="Confirm Resource Removal"
        bodyText="This will delete all action-permission records for this user type/resource row. Continue?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default PermissionComponent;
